import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ExternalLink, Copy, Eye, EyeOff, KeyRound, History, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { openExternal, copyText } from "@/lib/native";

export const STATUS_META = {
  ok: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  expiring: { label: "Expiring", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  expired: { label: "Expired", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  unknown: { label: "Saved", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  missing: { label: "No login", cls: "bg-white text-zinc-500 border-dashed border-zinc-300" },
};

export function VaultBadge({ status, daysLeft, tid }) {
  const m = STATUS_META[status] || STATUS_META.missing;
  const extra = status === "ok" || status === "expiring" ? ` · ${daysLeft}d` : status === "expired" ? ` · ${Math.abs(daysLeft)}d ago` : "";
  return <span data-testid={tid} className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${m.cls}`}><KeyRound size={10} />{m.label}{extra}</span>;
}

const copy = async (text, what) => {
  if (await copyText(text)) toast.success(`${what} copied`);
  else window.prompt(`Copy the ${what.toLowerCase()}:`, text);
};

export default function GstVaultDialog({ client, open, onOpenChange, isAdmin, onChanged }) {
  const [cred, setCred] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", password_updated_at: "", note: "" });

  useEffect(() => {
    if (!open || !client) return;
    setShowPwd(false); setShowHist(false); setEditing(false); setCred(null); setLoading(true);
    api.get(`/gst-vault/${client.id}`)
      .then(({ data }) => { setCred(data); setForm({ username: data.username, password: data.password, password_updated_at: "", note: data.note || "" }); })
      .catch((e) => { if (e.response?.status === 404) { setEditing(true); setForm({ username: "", password: "", password_updated_at: "", note: "" }); } else toast.error("Could not load credentials", { description: e.response?.data?.detail || e.message }); })
      .finally(() => setLoading(false));
  }, [open, client?.id]); // eslint-disable-line

  const save = async () => {
    try {
      const body = { username: form.username, password: form.password, note: form.note };
      if (form.password_updated_at) body.password_updated_at = new Date(form.password_updated_at).toISOString();
      const { data } = await api.put(`/gst-vault/${client.id}`, body);
      setCred(data); setEditing(false); setForm((f) => ({ ...f, password_updated_at: "" }));
      toast.success("GST login saved", { description: `Password valid till ${new Date(data.expires_at).toLocaleDateString("en-IN")}` });
      onChanged?.();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };

  const openPortal = async () => {
    await copy(cred.username, "Username");
    const win = await openExternal(cred.portal_url);
    toast.message("Portal opened", {
      description: "Username is on your clipboard. Paste it, then copy the password.",
      action: { label: "Copy password", onClick: () => copy(cred.password, "Password") },
      duration: 12000,
    });
    api.post(`/gst-vault/${client.id}/opened`).then(() => onChanged?.()).catch(() => {});
    if (!win) toast.error("Popup blocked — allow popups for this site");
  };

  const remove = async () => {
    if (!window.confirm(`Delete saved GST login for ${client.name}?`)) return;
    await api.delete(`/gst-vault/${client.id}`);
    toast.success("GST login deleted"); onChanged?.(); onOpenChange(false);
  };

  if (!client) return null;
  const m = cred ? STATUS_META[cred.status] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="gst-vault-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-2xl flex items-center gap-2"><ShieldCheck size={20} /> GST Portal Login</DialogTitle>
          <div className="text-sm text-zinc-600">{client.name}{client.gstin ? <span className="tabular text-xs ml-2 text-zinc-500">{client.gstin}</span> : null}</div>
        </DialogHeader>

        {loading ? <div className="text-sm text-zinc-500 py-6 text-center">Loading…</div> : null}

        {!loading && cred && !editing ? (
          <div className="space-y-4" data-testid="gst-vault-view">
            <div className={`border p-3 flex items-center justify-between gap-3 ${m.cls}`} data-testid="gst-vault-status">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold">Password status</div>
                <div className="text-sm font-semibold">
                  {cred.status === "expired" ? `Expired ${Math.abs(cred.days_left)} day(s) ago — update it on the portal, then save here`
                    : cred.status === "expiring" ? `Expires in ${cred.days_left} day(s) · ${new Date(cred.expires_at).toLocaleDateString("en-IN")}`
                    : cred.status === "ok" ? `Valid for ${cred.days_left} more days · till ${new Date(cred.expires_at).toLocaleDateString("en-IN")}`
                    : "Password date unknown"}
                </div>
              </div>
              <KeyRound size={18} />
            </div>

            <Row label="Username" value={cred.username} tid="gst-vault-username" onCopy={() => copy(cred.username, "Username")} />
            <Row label="Password" value={showPwd ? cred.password : "•".repeat(Math.min(cred.password.length, 14))} tid="gst-vault-password" mono
              onCopy={() => copy(cred.password, "Password")}
              extra={<button data-testid="gst-vault-toggle-pwd" onClick={() => setShowPwd((s) => !s)} className="p-1.5 hover:bg-zinc-100 rounded" title={showPwd ? "Hide" : "Show"}>{showPwd ? <EyeOff size={14} /> : <Eye size={14} />}</button>} />
            {cred.note ? <div className="text-xs text-zinc-600"><span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mr-2">Note</span>{cred.note}</div> : null}
            <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-1">
              <span>Password set {cred.password_updated_at ? new Date(cred.password_updated_at).toLocaleDateString("en-IN") : "—"}</span>
              <span>Saved by {cred.updated_by || "—"}</span>
              {cred.last_opened_at ? <span>Last opened {new Date(cred.last_opened_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} by {cred.last_opened_by}</span> : null}
            </div>

            <Button data-testid="gst-vault-open-portal-btn" onClick={openPortal} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-none h-11">
              <ExternalLink size={16} className="mr-2" /> Open GST Portal &amp; copy username
            </Button>
            <p className="text-[11px] text-zinc-500 -mt-2">gst.gov.in needs a CAPTCHA on every login, so paste the username, click “Copy password” in the toast, and solve the CAPTCHA.</p>

            {cred.history?.length ? (
              <div>
                <button data-testid="gst-vault-history-toggle" onClick={() => setShowHist((s) => !s)} className="text-xs font-semibold underline decoration-dotted flex items-center gap-1"><History size={13} /> {showHist ? "Hide" : "Show"} previous passwords ({cred.history.length})</button>
                {showHist ? (
                  <ul className="mt-2 border border-zinc-200 divide-y divide-zinc-100 text-xs" data-testid="gst-vault-history">
                    {cred.history.map((h, i) => (
                      <li key={i} className="px-3 py-2 flex items-center justify-between gap-2">
                        <div><span className="font-mono">{h.password}</span>{h.username !== cred.username ? <span className="text-zinc-400 ml-2">({h.username})</span> : null}</div>
                        <div className="text-[10px] text-zinc-500 text-right">{h.set_at ? new Date(h.set_at).toLocaleDateString("en-IN") : "—"} → {h.replaced_at ? new Date(h.replaced_at).toLocaleDateString("en-IN") : "—"}<br />by {h.replaced_by}</div>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {!loading && editing ? (
          <div className="space-y-3" data-testid="gst-vault-form">
            {!cred ? <p className="text-xs text-zinc-500">No GST portal login saved yet. Add it once — every GST executive can then open the portal without retyping.</p> : null}
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Portal username</Label>
              <Input data-testid="gst-vault-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoComplete="off" placeholder="GST portal user ID" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">{cred ? "New password" : "Password"}</Label>
              <div className="flex gap-1">
                <Input data-testid="gst-vault-password-input" type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
                <Button type="button" variant="outline" className="rounded-none border-zinc-300 px-2.5" onClick={() => setShowPwd((s) => !s)}>{showPwd ? <EyeOff size={14} /> : <Eye size={14} />}</Button>
              </div>
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Password changed on <span className="text-zinc-400 normal-case tracking-normal font-normal">(optional, defaults to today)</span></Label>
              <Input data-testid="gst-vault-date-input" type="date" value={form.password_updated_at} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, password_updated_at: e.target.value })} />
              <p className="text-[11px] text-zinc-500 mt-1">GST passwords expire 120 days after they're set. We'll warn you 15 days before.</p>
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Note</Label>
              <Input data-testid="gst-vault-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. OTP goes to owner's mobile" />
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          {!loading && cred && !editing ? (<>
            {isAdmin ? <Button data-testid="gst-vault-delete-btn" variant="ghost" onClick={remove} className="text-rose-600 hover:text-rose-700 mr-auto"><Trash2 size={14} className="mr-1" /> Delete</Button> : null}
            <Button data-testid="gst-vault-edit-btn" variant="outline" onClick={() => { setEditing(true); setShowPwd(false); setForm((f) => ({ ...f, password: "" })); }} className="rounded-none border-zinc-300">Update &amp; Save</Button>
          </>) : null}
          {!loading && editing ? (<>
            {cred ? <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button> : <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>}
            <Button data-testid="gst-vault-save-btn" onClick={save} disabled={!form.username.trim() || !form.password.trim()} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">Save login</Button>
          </>) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, tid, onCopy, extra, mono }) {
  return (
    <div className="flex items-center gap-2 border border-zinc-200 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{label}</div>
        <div data-testid={tid} className={`text-sm truncate ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      {extra}
      <button data-testid={`${tid}-copy`} onClick={onCopy} className="p-1.5 hover:bg-zinc-100 rounded" title={`Copy ${label.toLowerCase()}`}><Copy size={14} /></button>
    </div>
  );
}
